import { fetchAdminUserLandingDomains } from '@/api/admin';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingDomain } from '@/entities/landing.entity';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import LandingDomainStatusBadge from '@/pages/landings/landing-domain-status-badge';
import { GlobeIcon, LayoutTemplateIcon, LinkIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface LandingsDomainsSubtabProps {
	profileId: number;
	fetchDomains?: (profileId: number) => Promise<ILandingDomain[]>;
}

export default function LandingsDomainsSubtab({
	profileId,
	fetchDomains = fetchAdminUserLandingDomains,
}: LandingsDomainsSubtabProps) {
	const [domains, setDomains] = useState<ILandingDomain[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchDomains(profileId);
			setDomains(data);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [profileId, fetchDomains]);

	useEffect(() => {
		void load(true);
	}, [load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<LandingsErrorState
				message='Лендинги недоступны. Проверьте сервис landing и LANDING_INTERNAL_SECRET.'
				onRetry={() => void load(true)}
			/>
		);
	}

	if (domains.length === 0) {
		return (
			<LandingsEmptyState
				icon={GlobeIcon}
				title='Доменов нет'
				description='Пользователь ещё не добавил домены лендингов.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-3'>
			{domains.map(domain => (
				<div key={domain.id} className='rounded-xl border border-border bg-card p-3 shadow-sm'>
					<div className='flex items-start justify-between gap-2'>
						<div className='min-w-0 flex-1'>
							<div className='flex items-center gap-2'>
								<GlobeIcon size={16} className='shrink-0 text-muted-foreground' />
								<p className='truncate font-medium'>{domain.zoneName}</p>
							</div>
							{domain.errorMessage ? (
								<p className='mt-1 text-xs text-destructive line-clamp-2'>{domain.errorMessage}</p>
							) : null}
						</div>
						<LandingDomainStatusBadge status={domain.status} />
					</div>
					<div className='mt-3 flex items-center gap-3 border-t border-border/60 pt-3 text-xs text-muted-foreground'>
						<span className='flex items-center gap-1.5'>
							<LayoutTemplateIcon size={14} />
							{domain.sitesCount ?? 0} сайтов
						</span>
						<span className='flex items-center gap-1.5'>
							<LinkIcon size={14} />
							{domain.linksCount ?? 0} ссылок
						</span>
						<span>{new Date(domain.createdAt).toLocaleDateString('ru-RU')}</span>
					</div>
				</div>
			))}
		</div>
	);
}
